import { state } from './state.js';
import { handleLibSelect } from './library.js';
import { addFilesToQueue, startLibraryUpload, stopLibraryUpload, traverseFileTree } from './library_upload.js';
import { openCreateLibraryModal } from './modal_manager.js';
import { UI_COPY } from './config/uiCopy.js';
import { showToast } from './ui/toast.js';

export function initLibraryUploadHandlers() {
    const libDrop = document.getElementById('libDropZone');
    if (!libDrop) return;
    const fileInput = document.getElementById('libFiles');
    const folderInput = document.getElementById('libFolders');
    const uploadPhotosBtn = document.getElementById('libUploadPhotosBtn');
    const uploadFoldersBtn = document.getElementById('libUploadFoldersBtn');
    const overlayPhotosBtn = document.getElementById('libOverlayPhotosBtn');
    const overlayFoldersBtn = document.getElementById('libOverlayFoldersBtn');
    const stopBtn = document.getElementById('libUploadStopBtn');

    const ensureLibrarySelected = async ({ allowCreateIfEmpty = false } = {}) => {
        if (state.selectedLibraryId) return { ok: true, created: false };
        const canSelectExisting = !state.isAnonymous && Array.isArray(state.libraries) && state.libraries.length > 0;
        if (!canSelectExisting && allowCreateIfEmpty) {
            const res = await openCreateLibraryModal();
            if (res && res.name) {
                await handleLibSelect(null, res.name, res.isPublic);
                return { ok: Boolean(state.selectedLibraryId), created: Boolean(state.selectedLibraryId) };
            }
            return { ok: false, created: false };
        }
        showToast('Action needed', UI_COPY.alerts.selectOrCreateLibrary, 2400);
        return { ok: false, created: false };
    };

    const openPhotosPicker = async ({ allowCreateIfEmpty = false, skipPickerIfCreated = false } = {}) => {
        const ensure = await ensureLibrarySelected({ allowCreateIfEmpty });
        if (!ensure.ok) return;
        if (skipPickerIfCreated && ensure.created) return;
        if (fileInput) fileInput.click();
    };

    const openFoldersPicker = async ({ allowCreateIfEmpty = false, skipPickerIfCreated = false } = {}) => {
        const ensure = await ensureLibrarySelected({ allowCreateIfEmpty });
        if (!ensure.ok) return;
        if (skipPickerIfCreated && ensure.created) return;
        if (folderInput) folderInput.click();
    };

    libDrop.addEventListener('dragover', e => {
        e.preventDefault();
        libDrop.classList.add('is-dragover');
    });

    libDrop.addEventListener('dragleave', () => {
        libDrop.classList.remove('is-dragover');
    });

    libDrop.addEventListener('drop', async e => {
        e.preventDefault();
        libDrop.classList.remove('is-dragover');
        const ensure = await ensureLibrarySelected({ allowCreateIfEmpty: true });
        if (!ensure.ok) return;
        if (ensure.created) return;

        const items = e.dataTransfer.items;
        if (items) {
            for (let i = 0; i < items.length; i++) {
                const item = items[i].webkitGetAsEntry();
                if (item) await traverseFileTree(item);
            }
            startLibraryUpload();
        }
    });

    libDrop.addEventListener('click', async (e) => {
        const clickedButton = e.target && typeof e.target.closest === 'function'
            ? e.target.closest('#libUploadPhotosBtn, #libUploadFoldersBtn, #libOverlayPhotosBtn, #libOverlayFoldersBtn')
            : null;
        if (clickedButton) return;

        if (!state.selectedLibraryId) {
            const res = await openCreateLibraryModal();
            if (res && res.name) {
                await handleLibSelect(null, res.name, res.isPublic);
            }
            return;
        }

        await openPhotosPicker();
    });

    if (uploadPhotosBtn) {
        uploadPhotosBtn.addEventListener('click', async (e) => {
            e.stopPropagation();
            await openPhotosPicker({ allowCreateIfEmpty: true, skipPickerIfCreated: true });
        });
    }
    if (uploadFoldersBtn) {
        uploadFoldersBtn.addEventListener('click', async (e) => {
            e.stopPropagation();
            await openFoldersPicker({ allowCreateIfEmpty: true, skipPickerIfCreated: true });
        });
    }
    if (overlayPhotosBtn) {
        overlayPhotosBtn.addEventListener('click', async (e) => {
            e.stopPropagation();
            await openPhotosPicker();
        });
    }
    if (overlayFoldersBtn) {
        overlayFoldersBtn.addEventListener('click', async (e) => {
            e.stopPropagation();
            await openFoldersPicker();
        });
    }

    if (stopBtn) {
        stopBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            stopLibraryUpload();
        });
    }

    // File Input Handlers
    if (folderInput) {
        folderInput.addEventListener('change', e => {
            if (!state.selectedLibraryId) return;
            addFilesToQueue(e.target.files);
            startLibraryUpload();
            e.target.value = '';
        });
    }

    if (fileInput) {
        fileInput.addEventListener('change', e => {
            if (!state.selectedLibraryId) return;
            addFilesToQueue(e.target.files);
            startLibraryUpload();
            e.target.value = '';
        });
    }
}
